import React, { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Provider } from "react-redux";
import store from "./store";

import "./assets/css/library/bootstrap.min.css";
import "./assets/css/main.css";

// Shell components – always needed
import Modal from "./components/Modal";
import RequireAuth from "./components/RequireAuth";
import BasicSetup from "./components/BasicSetup";

// Lazy-loaded page components – each becomes a separate chunk
const NotFoundPage = lazy(() => import("./pages/404.js"));
const Home = lazy(() => import("./pages/Home"));
const Auth = lazy(() => import("./pages/Auth"));
const CompleteUserProfile = lazy(() => import("./pages/Auth/CompleteUserProfile"));
const Profile = lazy(() => import("./pages/Profile/ProfileInformation"));
const UpdateProfile = lazy(() => import("./pages/Profile/UpdateProfile"));
const LoadPost = lazy(() => import("./pages/LoadPost"));
const Chat = lazy(() => import("./pages/Chat"));
const Transaction = lazy(() => import("./pages/Transaction/Transaction"));
const Notification = lazy(() => import("./pages/Notification/Notification"));
const SearchCarrier = lazy(() => import("./pages/Search/SearchCarrier"));
const SearchLoadPost = lazy(() => import("./pages/Search/SearchLoadPost"));
const Order = lazy(() => import("./pages/Order"));
const Carrier = lazy(() => import("./pages/Carrier"));
const Withdraw = lazy(() => import("./pages/Withdraw"));
const Payment = lazy(() => import("./pages/Payment"));
const Favorite = lazy(() => import("./pages/Favorite"));
const Contact = lazy(() => import("./pages/Contact"));
const PaypalWeb = lazy(() => import("./PaypalWeb"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const UserReviews = lazy(() => import("./pages/UserReviews"));
const Wallet = lazy(() => import("./pages/Wallet"));
const AccountInformation = lazy(() => import("./pages/Profile/AccountInformation"));
const Performance = lazy(() => import("./pages/Carrier/Performance"));
const CarrierPendingOffers = lazy(() => import("./pages/Carrier/Monitor"));
const UserProfileInformation = lazy(() => import("./pages/Profile/UserProfileInformation"));

function App() {
  const [clint_width, setClint_width] = useState(window.innerWidth);

  const detectSize = () => {
    setClint_width(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", detectSize);
    return () => {
      window.removeEventListener("resize", detectSize);
    };
  }, [clint_width]);

  return (
    // Bydefault all componet would contains header and footer but we can disable it
    // by passing isHeaderRequired and isFoterRequired props to RequireAuth component

    <Provider store={store}>
      <BrowserRouter>
        <div
          style={{
            maxWidth: clint_width,
            overflowX: "hidden",
          }}
        >
          <BasicSetup />
          <div className="mobile-bg"></div>{" "}
          {/* background effect for mobile menu */}
          <Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>}>
          <Routes>
            <Route path="/auth/*" element={<Auth />} />
            <Route
              path="/chat/*"
              element={
                <RequireAuth isFoterRequired={false}>
                  <Chat />
                </RequireAuth>
              }
            />
            <Route
              path="/profile-information"
              element={
                <RequireAuth>
                  <Profile />
                </RequireAuth>
              }
            />
            <Route
              path="/account-information"
              element={
                <RequireAuth>
                  <AccountInformation />
                </RequireAuth>
              }
            />
            <Route
              path="/:userID/user-profile-information"
              element={
                <RequireAuth>
                  <UserProfileInformation />
                </RequireAuth>
              }
            />
            <Route
              path="/complete-user-profile"
              element={
                <RequireAuth>
                  <CompleteUserProfile />
                </RequireAuth>
              }
            />
            <Route
              path="/update-profile/*"
              element={
                <RequireAuth>
                  <UpdateProfile />
                </RequireAuth>
              }
            />
            <Route
              path="/load-post/*"
              element={
                <RequireAuth>
                  <LoadPost />
                </RequireAuth>
              }
            />
            <Route
              path="/transaction"
              element={
                <RequireAuth>
                  <Transaction />
                </RequireAuth>
              }
            />
            <Route
              path="/notification"
              element={
                <RequireAuth>
                  <Notification />
                </RequireAuth>
              }
            />
            <Route
              path="/search-carrier"
              element={
                <RequireAuth>
                  <SearchCarrier />
                </RequireAuth>
              }
            />
            <Route
              path="/search-load-post"
              element={
                <RequireAuth>
                  <SearchLoadPost />
                </RequireAuth>
              }
            />
            <Route
              path="/order/*"
              element={
                <RequireAuth>
                  <Order />
                </RequireAuth>
              }
            />
            <Route
              path="/carrier/*"
              element={
                <RequireAuth>
                  <Carrier />
                </RequireAuth>
              }
            />
            <Route
              path="/withdraw/*"
              element={
                <RequireAuth>
                  <Withdraw />
                </RequireAuth>
              }
            />
            <Route
              path="/payment/:paymentRequestId/*"
              element={
                <RequireAuth>
                  <Payment />
                </RequireAuth>
              }
            />
            <Route
              path="/favorite/"
              element={
                <RequireAuth>
                  <Favorite />
                </RequireAuth>
              }
            />
            
            <Route
              path="/wallet/"
              element={
                <RequireAuth>
                  <Wallet />
                </RequireAuth>
              }
            />
            <Route
              path="/performance/"
              element={
                <RequireAuth>
                  <Performance />
                </RequireAuth>
              }
            />
            <Route
              path="/pending-offers/"
              element={
                <RequireAuth>
                  <CarrierPendingOffers />
                </RequireAuth>
              }
            />

            <Route
              path="/contact/"
              element={
                  <Contact />
              }
            />
            <Route
              path="/paypal-payment/:paymentRequestId/:authToken/*"
              element={<PaypalWeb />}
            />
            {/* /****************************************** This 2 path should be
          always on the bottom *********************************************/}
            <Route path="/" element={<Home />} />
            <Route path="/privacy-Policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="*" element={<NotFoundPage />} />
            {/* /******************************************
             *********************************************/}
          </Routes>
          </Suspense>
        </div>
      </BrowserRouter>

      <Modal />
    </Provider>
  );
}

export default App;
